import React, { FC, useEffect, useState } from 'react';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { PrinterStatusResponse, useBridgeApi, useI18n, usePrinterApi } from '~Hooks';
import { Box } from '~Styles';
import { formatPrinterStatus } from '~utilities';
import { Printer } from '~reducers';
import Icon from '~ComponentsImproved/Icon';
import PrinterIcon from '~Assets/icons/print.svg';
import { PRINTERS_WITHOUT_STATUS } from '~Components/PrinterManager';
import { StyledBox } from '../styles';
import { STATUS_COLORS, StatusType } from '..';
import Loader from '~ComponentsImproved/Loader';

type StatusColor = typeof STATUS_COLORS[keyof typeof STATUS_COLORS];

interface PrinterStatus {
  printerId: string;
  text: string;
  status: StatusType;
  color: StatusColor;
}

interface Props {
  printer: Printer;
}

const PrinterRow: FC<Props> = ({ printer }) => {
  const { i18n } = useI18n();

  const printerApi = usePrinterApi();
  const bridgeApi = useBridgeApi();

  const [loading, setLoading] = useState<boolean>(true);
  const [printerStatus, setPrinterStatus] = useState<PrinterStatus>();

  const getStatusesWithTimeout = async (
    printerId: string,
    timeout = 3000,
  ): Promise<PrinterStatusResponse> => {
    // Create a promise that rejects after the timeout
    const timeoutPromise: Promise<PrinterStatusResponse> = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout')), timeout),
    );

    return Promise.race([bridgeApi.getPrinterStatus(printerId), timeoutPromise]);
  };

  const getPrinterStatusText = async () => {
    /** Helper function to create PrinterStatus objects */
    const createStatus = (
      text: string,
      statusType: StatusType,
    ): { text: string; color: StatusColor; status: StatusType } => ({
      text,
      color: STATUS_COLORS[statusType] as StatusColor,
      status: statusType,
    });

    // Special cases first - handle based on printer properties
    if (!printer.primary && printer.stations.length === 0) {
      return createStatus(i18n('Printer.Status.NoStation'), 'WARNING');
    }

    if (printer.model === 'VIRTUAL_TEST') {
      return createStatus(i18n('Printer.Status.Online'), 'OK');
    }

    if (PRINTERS_WITHOUT_STATUS.includes(printer.model)) {
      return createStatus('', 'OK');
    }

    if (!printer.online) {
      return createStatus(i18n('Printer.Status.Offline'), 'ERROR');
    }

    // Handle the printer status API call
    try {
      const statuses: PrinterStatusResponse = await getStatusesWithTimeout(printer.id);

      const isError =
        statuses.isOffline ||
        statuses.isResponsive === false ||
        statuses.paperEmpty ||
        statuses.coverIsOpen;

      const isWarning = statuses.paperNearEmpty;

      const statusType: StatusType = isError ? 'ERROR' : isWarning ? 'WARNING' : 'OK';

      return {
        text: formatPrinterStatus(statuses, i18n),
        color: STATUS_COLORS[statusType] as StatusColor,
        status: statusType,
      };
    } catch (error) {
      // Both timeout and general errors return the same result
      return createStatus(i18n('Printer.Status.Offline'), 'ERROR');
    }
  };

  useEffect(() => {
    getPrinterStatusText().then((status) => {
      setPrinterStatus({
        printerId: printer.id,
        text: status.text,
        color: status.color,
        status: status.status,
      });
      setLoading(false);
    });
  }, [printer]);

  return (
    <StyledBox display="flex" alignItems="flex-start" gap="small" padding="small">
      {loading ? (
        <Loader color="var(--colorBlueGray1200)" size="sm" />
      ) : (
        <Icon src={PrinterIcon} color={printerStatus?.color} />
      )}

      <Box display="flex" flexDirection="column" alignItems="flex-start">
        <Heading level={4}>
          {printerApi.getNameFromId(printer.id) ?? printer.id}{' '}
          {printer.primary && ` (${i18n('Printer.Status.Primary')})`}{' '}
        </Heading>
        {printerStatus || !loading ? (
          <Paragraph level={5} color="var(--colorBlueGray1200)" style={{ whiteSpace: 'nowrap' }}>
            {printerStatus?.text}
          </Paragraph>
        ) : (
          <Paragraph level={5} color="var(--colorBlueGray1200)" style={{ whiteSpace: 'nowrap' }}>
            {i18n('PrinterStatus.Loading')}
          </Paragraph>
        )}
      </Box>
    </StyledBox>
  );
};

export default PrinterRow;
